import { useState } from 'react'
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react'
import { useAuditLog, type AuditLogEntry } from '../../api/admin-audit'

const PAGE_SIZE = 50

/**
 * "Audit log" tab — read-only view over the
 * ``billing_audit_log`` rows produced by every mutating endpoint under
 * ``/api/admin/billing/*``, ``/api/billing/*`` and
 * ``/api/admin/reconcile/*``.
 *
 * Filters are intentionally minimal (free-text actor email substring,
 * action prefix, ISO date range) — internal tooling, not user-facing
 * polish. Pagination is offset-based so a forgotten filter doesn't pull
 * thousands of rows in one request.
 */
export function AdminBillingAuditTab() {
  const [actorEmail, setActorEmail] = useState('')
  const [actionPrefix, setActionPrefix] = useState('')
  const [sinceLocal, setSinceLocal] = useState('') // datetime-local string
  const [untilLocal, setUntilLocal] = useState('')
  const [page, setPage] = useState(0)

  const since = parseLocalDateTime(sinceLocal)
  const until = parseLocalDateTime(untilLocal)

  const { data, isLoading, isFetching, error, refetch } = useAuditLog({
    action: actionPrefix || undefined,
    since,
    until,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })

  // Actor-email filter is applied client-side: the backend filters by
  // user_id, but the operator typically knows the email. Cheap to filter
  // a 50-row page in JS.
  const filteredItems = (data?.items ?? []).filter((row) => {
    if (!actorEmail) return true
    return row.actor_email.toLowerCase().includes(actorEmail.toLowerCase())
  })

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-border bg-surface p-4">
        <header className="mb-3 flex items-center gap-2 font-medium">
          <FileText className="h-4 w-4 text-text-secondary" />
          Filters
        </header>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="space-y-1 text-xs">
            <span className="text-text-secondary">Actor email contains</span>
            <input
              type="text"
              value={actorEmail}
              onChange={(e) => {
                setActorEmail(e.target.value)
                setPage(0)
              }}
              placeholder="admin@…"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-text-secondary">Action prefix</span>
            <input
              type="text"
              value={actionPrefix}
              onChange={(e) => {
                setActionPrefix(e.target.value)
                setPage(0)
              }}
              placeholder="pricing_config"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-text-secondary">From</span>
            <input
              type="datetime-local"
              value={sinceLocal}
              onChange={(e) => {
                setSinceLocal(e.target.value)
                setPage(0)
              }}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-text-secondary">Until</span>
            <input
              type="datetime-local"
              value={untilLocal}
              onChange={(e) => {
                setUntilLocal(e.target.value)
                setPage(0)
              }}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-lg border border-border bg-background px-3 py-1 text-xs hover:bg-surface-elevated disabled:opacity-50"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          <p className="text-xs text-text-secondary">
            Server-side filters: action prefix + time range. Actor email is
            filtered client-side after the page loads.
          </p>
        </div>
      </section>

      {error && (
        <div className="rounded-lg border border-red-500 bg-red-500/10 p-4 text-sm">
          Failed to load audit log: {String(error)}
        </div>
      )}

      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-surface-elevated text-xs uppercase tracking-wide text-text-secondary">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Actor</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Target org</th>
              <th className="px-3 py-2">Endpoint</th>
              <th className="px-3 py-2">Detail</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-text-secondary">
                  Loading…
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-text-secondary">
                  No audit entries match the current filters.
                </td>
              </tr>
            ) : (
              filteredItems.map((row) => <AuditRow key={row.id} row={row} />)
            )}
          </tbody>
        </table>
      </section>

      <nav className="flex items-center justify-between text-sm">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0 || isFetching}
          className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-3 py-1 hover:bg-surface-elevated disabled:opacity-50"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Prev
        </button>
        <span className="text-text-secondary">
          Page {page + 1} · showing {filteredItems.length} of{' '}
          {data?.items.length ?? 0} on this page
        </span>
        <button
          onClick={() => setPage((p) => p + 1)}
          // The backend doesn't return total count; "more pages exist" is
          // approximated by "the current page is full".
          disabled={(data?.items.length ?? 0) < PAGE_SIZE || isFetching}
          className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-3 py-1 hover:bg-surface-elevated disabled:opacity-50"
        >
          Next <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </nav>
    </div>
  )
}

function AuditRow({ row }: { row: AuditLogEntry }) {
  const when = new Date(row.timestamp * 1000)
  return (
    <tr className="border-b border-border last:border-b-0 align-top">
      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
        {when.toISOString().replace('T', ' ').slice(0, 19)}
      </td>
      <td className="px-3 py-2">
        <div className="font-medium">{row.actor_email}</div>
        <div className="text-xs text-text-secondary">
          {row.actor_source}
          {row.actor_is_superadmin ? ' · superadmin' : ''}
        </div>
      </td>
      <td className="px-3 py-2 font-mono text-xs">{row.action}</td>
      <td className="px-3 py-2 font-mono text-xs">{row.target_org_id ?? '—'}</td>
      <td className="px-3 py-2 font-mono text-xs text-text-secondary">
        {row.endpoint}
      </td>
      <td className="px-3 py-2">
        <details>
          <summary className="cursor-pointer text-xs text-text-secondary hover:text-text-primary">
            Show
          </summary>
          <div className="mt-2 space-y-2">
            <DetailBlock label="Before" value={row.before_state} />
            <DetailBlock label="After" value={row.after_state} />
            <DetailBlock label="Metadata" value={row.metadata} />
            <div className="text-xs text-text-secondary">
              request_id: <span className="font-mono">{row.request_id ?? '—'}</span>
              {' · '}
              ip: <span className="font-mono">{row.ip_address ?? '—'}</span>
            </div>
          </div>
        </details>
      </td>
    </tr>
  )
}

function DetailBlock({
  label,
  value,
}: {
  label: string
  value: Record<string, unknown> | null
}) {
  if (value === null) return null
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-text-secondary">
        {label}
      </div>
      <pre className="mt-1 max-w-xl overflow-x-auto rounded border border-border bg-background p-2 font-mono text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

/** Convert an ``<input type="datetime-local">`` value to epoch seconds. */
function parseLocalDateTime(local: string): number | undefined {
  if (!local) return undefined
  const ms = new Date(local).getTime()
  if (Number.isNaN(ms)) return undefined
  return ms / 1000
}
